import React, { useEffect, useState, useRef, useContext } from "react";
import { Route, Routes, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ApiContext } from "../../contexts/ApiContext";
import { OrderAlertsProvider } from "../../contexts/OrderAlertsContext";
import OrderAlertStack from "../../components/OrderAlerts/OrderAlertStack";
import MirandaChatFab from "../../components/Miranda/MirandaChatFab";
import PerfectScrollbar from "perfect-scrollbar";
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import FixedPlugin from "components/FixedPlugin/FixedPlugin.js";
import routes from "routes.js";
import { BackgroundColorContext } from "contexts/BackgroundColorContext";
import { accountPath } from "../../utils/accountPath";
import { buildBlockedAccountWhatsappUrl } from "../../utils/planWhatsapp";
import mirandaLogo from "assets/img/miranda-logo.svg";
import "./account-blocked.css";

function Admin(props) {
  const location = useLocation();
  const { accountId: accountIdParam } = useParams();
  const accountId = Number(accountIdParam);
  const mainPanelRef = useRef(null);
  const [sidebarOpened, setsidebarOpened] = useState(
    document.documentElement.className.indexOf("nav-open") !== -1
  );
  const [accountBlocked, setAccountBlocked] = useState(false);

  const navigate = useNavigate();
  const { token, user, account, isSuperAdmin, homeAccountId, loadActiveAccount } = useContext(ApiContext);
  const accountReady = Boolean(account && account.id === accountId);

  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token || !user || !accountId || Number.isNaN(accountId)) return;

    if (!isSuperAdmin && homeAccountId && accountId !== homeAccountId) {
      navigate(accountPath(homeAccountId, '/dashboard'), { replace: true });
      return;
    }

    if (account?.id === accountId) {
      setAccountBlocked(Boolean(account?.adminBlocked && !isSuperAdmin));
      return;
    }

    let cancelled = false;

    loadActiveAccount(accountId)
      .then((acc) => {
        if (cancelled) return;
        setAccountBlocked(Boolean(acc?.adminBlocked && !isSuperAdmin));
      })
      .catch(() => {
        if (cancelled || isSuperAdmin) return;
        navigate(accountPath(homeAccountId, '/dashboard'), { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [token, user?.id, accountId, account?.id, account?.adminBlocked, isSuperAdmin, homeAccountId, loadActiveAccount, navigate]);

  const panelReady = accountReady && !accountBlocked;

  useEffect(() => {
    if (!panelReady || navigator.platform.indexOf("Win") === -1) return;

    document.documentElement.className += " perfect-scrollbar-on";
    document.documentElement.classList.remove("perfect-scrollbar-off");

    const instances = [];
    if (mainPanelRef.current) {
      instances.push(new PerfectScrollbar(mainPanelRef.current, { suppressScrollX: true }));
    }

    return function cleanup() {
      instances.forEach((instance) => instance.destroy());
      document.documentElement.classList.add("perfect-scrollbar-off");
      document.documentElement.classList.remove("perfect-scrollbar-on");
    };
  }, [panelReady]);

  useEffect(() => {
    if (!panelReady) return;

    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainPanelRef.current) {
      mainPanelRef.current.scrollTop = 0;
    }
  }, [location, panelReady]);

  const toggleSidebar = () => {
    document.documentElement.classList.toggle("nav-open");
    setsidebarOpened(!sidebarOpened);
  };

  const layoutPrefix = accountPath(accountId, '');

  const isModuleOff = (route) =>
    accountReady &&
    ((route.requiresTables && !account?.tablesEnabled) ||
      (route.requiresInventory && !account?.inventoryEnabled) ||
      (route.requiresPrinting && !account?.printingEnabled));

  const routeElement = (route) =>
    isModuleOff(route) ? <Navigate to={accountPath(accountId, '/dashboard')} replace /> : route.component;

  const getRoutes = (routeList) => {
    const result = [];
    routeList.forEach((prop, key) => {
      if (prop.collapse && prop.views) {
        prop.views.forEach((sub, subKey) => {
          result.push(
            <Route path={sub.path.replace(/^\//, '')} element={routeElement(sub)} key={`${key}-${subKey}`} />
          );
        });
      } else if (prop.component) {
        result.push(
          <Route path={prop.path.replace(/^\//, '')} element={routeElement(prop)} key={key} />
        );
      }
    });
    return result;
  };

  const getBrandText = (path) => {
    for (let i = 0; i < routes.length; i++) {
      const route = routes[i];
      if (route.collapse && route.views) {
        for (let j = 0; j < route.views.length; j++) {
          const sub = route.views[j];
          if (path.indexOf(layoutPrefix + sub.path) !== -1) {
            return sub.name;
          }
        }
      }
      if (path.indexOf(layoutPrefix + route.path) !== -1) {
        return route.name;
      }
    }
    return "Brand";
  };

  if (accountBlocked) {
    const supportWhatsappUrl = buildBlockedAccountWhatsappUrl({
      accountName: account?.name,
      accountId: account?.id || accountId,
    });

    return (
      <div className="account-blocked">
        <div className="account-blocked__glow account-blocked__glow--tl" aria-hidden="true" />
        <div className="account-blocked__glow account-blocked__glow--br" aria-hidden="true" />
        <div className="account-blocked__card">
          <div className="account-blocked__brand">
            <img src={mirandaLogo} alt="Miranda Software" />
          </div>
          <div className="account-blocked__body">
            <span className="account-blocked__badge">Acesso suspenso</span>
            <h1 className="account-blocked__title">
              Acesso temporariamente suspenso
            </h1>
            <p className="account-blocked__text">
              O painel desta conta está bloqueado pela equipe Miranda Software.
              Isso costuma ocorrer por pendências administrativas ou financeiras.
            </p>
            <p className="account-blocked__text">
              Fale conosco pelo WhatsApp — vamos te ajudar a regularizar e
              liberar o acesso o quanto antes.
            </p>
            <a
              className="account-blocked__whatsapp"
              href={supportWhatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M16.04 3C9.4 3 4 8.36 4 14.95c0 2.11.55 4.16 1.6 5.97L4 29l8.3-2.17a12.1 12.1 0 0 0 3.74.59c6.64 0 12.04-5.36 12.04-11.95C28.08 8.36 22.68 3 16.04 3Zm0 21.8c-1.2 0-2.37-.31-3.4-.91l-.24-.14-4.93 1.29 1.32-4.78-.16-.25a9.7 9.7 0 0 1-1.5-5.16c0-5.37 4.4-9.74 9.91-9.74s9.91 4.37 9.91 9.74-4.4 9.95-9.91 9.95Zm5.43-7.28c-.3-.15-1.76-.86-2.03-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.15-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.46-.88-.78-1.47-1.74-1.64-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.91-2.19-.24-.58-.48-.5-.67-.5h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.46s1.07 2.85 1.21 3.05c.15.2 2.1 3.18 5.08 4.46.71.3 1.26.48 1.7.62.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z"
                />
              </svg>
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <BackgroundColorContext.Consumer>
      {({ color, changeColor }) => (
        <OrderAlertsProvider accountId={accountId}>
        <React.Fragment>
          <div className="wrapper">
            <Sidebar
              routes={routes}
              toggleSidebar={toggleSidebar}
              layoutPrefix={layoutPrefix}
            />
            <div className="main-panel" ref={mainPanelRef} data={color}>
              <AdminNavbar
                brandText={getBrandText(location.pathname)}
                toggleSidebar={toggleSidebar}
                sidebarOpened={sidebarOpened}
              />
              <Routes>
                {getRoutes(routes)}
                <Route
                  path="/"
                  element={<Navigate to="dashboard" replace />}
                />
              </Routes>
              <FixedPlugin bgColor={color} handleBgClick={changeColor} />
              <OrderAlertStack accountId={accountId} />
              {panelReady && <MirandaChatFab accountId={accountId} />}
            </div>
          </div>
        </React.Fragment>
        </OrderAlertsProvider>
      )}
    </BackgroundColorContext.Consumer>
  );
}

export default Admin;
